import type { GameProductShort } from '@patrianna/shared-patrianna-types/store/GameModule'
import { nextTick } from '@patrianna/shared-utils'
import cx from 'classnames'
import type { ReactNode, MouseEvent } from 'react'

import images from 'config/images.json'
import FavoriteButton from 'containers/FavoriteButton'
import favoriteButtonClasses from 'containers/FavoriteButton/styles.module.scss'
import { JackpotTickerContainer } from 'containers/JackpotsFromProviders'
import { BadgeSlot, type BadgeSlotClasses } from 'src/components/BadgeSlot'
import { buildGameBadges } from 'src/utils/buildGameBadges'
import { useAppSelector } from 'store/hooks'
import { isLoggedInSelector } from 'store/modules/user/selectors'
import type { SupportedCurrencies } from 'types/currencies'
import { hasTag } from 'utils/gameTags'

import { Picture } from '../Picture'
import { ImageType } from '../Picture/Picture.types'
import type { PictureProps } from '../Picture/Picture.types'

import MoreInfoBtn from './MoreInfoBtn'
import classes from './styles.module.scss'

type Props = {
  game: GameProductShort
  role?: string
  onClick?: (event: MouseEvent<HTMLDivElement>) => void
  activeCurrency: SupportedCurrencies
  className?: string
  with3DImage?: boolean
  showFavorite?: boolean
  hasJackpotTicker?: boolean
  jackpotElement?: ReactNode
}

export default function ProductTile(props: Props) {
  const loggedIn = useAppSelector(isLoggedInSelector)
  const gameTags = props.game.tags
  const topTag = hasTag(gameTags, 'top')
  const newTag = hasTag(gameTags, 'new')
  // @ts-ignore ToDo: needs to add EXL tag to @Patrianna TagsType
  const exclusiveTag = hasTag(gameTags, 'EXL')
  const showNew = newTag && !exclusiveTag
  const showTop = topTag && !exclusiveTag
  const hasJackpotTicker = Boolean(props.hasJackpotTicker)

  const { staticExclusiveItem, animatedBadges } = buildGameBadges({
    exclusiveTag,
    showNew,
    showTop,
    hasJackpotTicker,
    jackpotElement: props.jackpotElement ?? (
      <JackpotTickerContainer
        key='jackpot-ticker'
        game={props.game}
        className={cx(classes.tagItem, classes.tagJackpot)}
      />
    ),
    classes: classes as BadgeSlotClasses,
  })

  const version = exclusiveTag ? '?v=1' : ''

  const pictureProps: PictureProps = {
    src: props?.with3DImage
      ? `${images.bucket}/ysi-images/3d-tiles/${props.game.code}.png`
      : `${images.bucket}/tiles/${props.game.code}/source.jpg${version}`,
    alt: props.game.title,
    className: cx(classes.image, props.with3DImage && classes.image3D),
    type: props?.with3DImage ? ImageType['3D_TILE'] : ImageType.TILE,
    loading: 'lazy',
    version,
    isWebpFirst: !props.with3DImage, // disabled for 3D tiles to improve images quality
  }

  const handleTileClick = (e: MouseEvent<HTMLDivElement>) => {
    nextTick().then(() => {
      if (props.onClick) {
        props.onClick(e)
      }
    })
  }

  return (
    <div className={cx(classes.root, props?.className)} onClick={handleTileClick}>
      <div
        className={props.with3DImage ? classes.imageContainer3D : classes.imageContainer}
        data-test={`game-tile-${props.game.code}`}
        data-testid={`game-tile-${props.game.code}`}
      >
        <div className={cx(classes.topLeftContainer, props.with3DImage && classes.tag3D)}>
          {props.showFavorite && (
            <FavoriteButton
              className={favoriteButtonClasses.tileFavorite}
              gameProduct={props.game}
              refererPath='game_tile'
            />
          )}
        </div>
        <div
          className={cx(
            classes.topRightContainer,
            props.showFavorite && classes.withFavorite,
            props.with3DImage && classes.tag3D,
          )}
        >
          <BadgeSlot
            animatedBadges={animatedBadges}
            staticItem={staticExclusiveItem}
            classes={classes as BadgeSlotClasses}
          />
          {props.game.moreInfo && !loggedIn && <MoreInfoBtn game={props.game} activeCurrency={props.activeCurrency} />}
        </div>
        <Picture {...pictureProps} />
      </div>

      {!props.with3DImage && <p className={classes.title}>{props.game.title}</p>}
    </div>
  )
}
